import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../API/Api";
import "../housestyle.css";
import "./SeasonInfo.css";
import "../Home/Home.css";
import DiscordMarkdown from "../components/DiscordMarkdown";
import { FadeIn, StaggerContainer, StaggerItem } from "../components/Animations";
import { McIcon } from "../components/CraftingGrid";

const VIDEO = "https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_2.mp4";
const HYPE_START = "2026-02-01T00:00:00";

// floating deco icons on the hero's right side
const HERO_DECO = [
  { mat: 'grass_block',     color: '#7cba46', rotate: -14, top: 16, left: 12 },
  { mat: 'netherite_sword', color: '#d94f4f', rotate:  12, top:  6, left: 62 },
  { mat: 'zombie_head',     color: '#a070e0', rotate:  -5, top: 44, left: 38 },
  { mat: 'chest_minecart',  color: '#f0ab20', rotate:   8, top: 68, left: 70 },
  { mat: 'lodestone',       color: '#7fd4ff', rotate:  -9, top: 74, left: 8 },
];

function useCountdown(targetDate) {
  const calc = () => {
    if (!targetDate) return { days: 0, hours: 0, minutes: 0, seconds: 0, ended: false };
    const diff = new Date(targetDate) - new Date();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, ended: true };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
      ended: false,
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    if (!targetDate) return;
    const t = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(t);
  }, [targetDate]);
  return time;
}

function autoProgress(launchDate) {
  const start = new Date(HYPE_START).getTime();
  const end = new Date(launchDate).getTime();
  const now = Date.now();
  if (now >= end) return 100;
  if (now <= start) return 0;
  return Math.round(((now - start) / (end - start)) * 100);
}

function formatLaunchFriendly(iso) {
  if (!iso) return "Yet to be announced";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    + " · " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function NewsCard({ item }) {
  const isOffer = item.type === 'offer';
  return (
    <div className={`home-news-card ${isOffer ? 'home-news-card--offer' : ''} ${item.image_url ? 'home-news-card--has-img' : ''}`}>
      <div className="home-news-content">
        <div className="home-news-header">
          <span className={`home-news-badge ${item.type}`}>
            {isOffer ? 'Limited Offer' : item.type}
          </span>
          <span className="home-news-date">
            {new Date(item.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </span>
        </div>
        <h3>{item.title}</h3>
        <DiscordMarkdown text={item.content} className="home-news-body" />
        <div className="home-news-footer">
          <span className="home-news-author">By {item.author || 'Staff'}</span>
        </div>
      </div>
      {item.image_url && (
        <div className="home-news-img">
          <img src={item.image_url} alt={item.title} />
        </div>
      )}
    </div>
  );
}

function RootChip({ root }) {
  return (
    <Link to={`/wiki/root/${root.slug}`} className="si-root-chip" style={{ '--rc': root.color }}>
      <div className="si-root-chip-orb">
        <McIcon materialKey={root.material} size={32} />
      </div>
      <div className="si-root-chip-text">
        <span className="si-root-chip-name">{root.name}</span>
        <span className="si-root-chip-style">{root.playstyle || 'Root'}</span>
      </div>
    </Link>
  );
}

export default function SeasonInfo() {
  const [season, setSeason] = useState(null);
  const [news, setNews] = useState([]);
  const [roots, setRoots] = useState([]);
  const [copied, setCopied] = useState(false);

  function copyIP() {
    navigator.clipboard.writeText(season?.ip || 'play.simplemcnetwork.net').catch(() => { });
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  useEffect(() => {
    api("getseasoninfo").then((res) => {
      if (res.isSuccess) setSeason(res.data);
    });
    api("getnews").then((res) => {
      if (res.isSuccess) setNews(res.data || []);
    });
    api("getroots").then((res) => {
      if (res.isSuccess) setRoots(res.data || []);
    });
  }, []);

  const visibleRoots = roots.filter(r => Number(r.enabled) !== 0 && r.status !== 'stub');
  const marqueeA = visibleRoots.filter((_, i) => i % 2 === 0);
  const marqueeB = visibleRoots.filter((_, i) => i % 2 === 1);

  const launchDate = season?.start_date ?? null;
  const countdown = useCountdown(launchDate);
  const progress = season
    ? (season.progress !== null && season.progress !== undefined
      ? Number(season.progress)
      : autoProgress(launchDate))
    : 0;
  const workingOn = season?.working_on ?? [];
  const isLive = season?.is_live === "1" || season?.is_live === 1;
  const launchLabel = launchDate ? formatLaunchFriendly(launchDate) : "Yet to be announced";
  // only meaningful with an explicit progress value or a launch date to interpolate from
  const showProgress = season && (season.progress !== null && season.progress !== undefined ? true : !!launchDate);

  // IP is featured separately in the panel header
  const stats = season ? [
    ["Version",       season.version,                                      'grass_block',    '#7cba46'],
    ["Map Size",      season.map_size,                                     'filled_map',     '#f5d848'],
    ["Seed",          season.seed,                                         'wheat_seeds',    '#52c078'],
    ["Player Slots",  season.player_slots,                                 'player_head',    '#5a9ee0'],
    ["Roots",         season.root_count ?? (visibleRoots.length || null),  'nether_star',    '#8a4dff'],
    ["Supply Drops",  season.supply_drops ?? 'Every 2 hours',              'chest_minecart', '#f0ab20'],
    ["Meteor Events", season.meteor_events ?? 'Every 30 min',              'magma_block',    '#e8642c'],
  ].filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== "") : [];

  return (
    <>
      <div className="si-hero">
        <video className="si-hero-video" src={VIDEO} autoPlay muted loop playsInline />
        <div className="si-hero-overlay" />
        <FadeIn className="si-hero-content container" y={26} duration={0.8}>

          <div className="si-hero-eyebrow-row">
            <span className="sec-eyebrow sec-eyebrow--left">Current Season</span>
          </div>
          <h1 className="si-hero-title">Season <em>{season?.number ?? 2}</em></h1>

          {launchDate && (
            <div className="si-hero-launch-label">
              Expected launch: <span>{launchLabel}</span>
            </div>
          )}

          {/* Countdown / live / date unknown */}
          {isLive || (launchDate && countdown.ended) ? (
            <div className="si-hero-live-badge">
              <span className="si-live-dot" />
              Season {season?.number ?? 2} is LIVE · play.simplemcnetwork.net
            </div>
          ) : !launchDate ? (
            <div className="si-hero-tba">
              <span className="si-hero-tba-big">Yet to be announced</span>
              <span className="si-hero-tba-sub">Keep an eye on the Discord for the launch date</span>
            </div>
          ) : (
            <div className="si-hero-countdown">
              {[
                ["Days", countdown.days],
                ["Hours", countdown.hours],
                ["Minutes", countdown.minutes],
                ["Seconds", countdown.seconds],
              ].map(([label, val]) => (
                <div key={label} className="si-cd-block">
                  <span className="si-cd-val">{String(val).padStart(2, "0")}</span>
                  <span className="si-cd-label">{label}</span>
                </div>
              ))}
            </div>
          )}

          {showProgress && (
            <>
              <div className="si-hero-progress-wrap">
                <div className="si-hero-progress-bar">
                  <div className="si-hero-progress-fill" style={{ width: `${progress}%` }} />
                </div>
                <span className="si-hero-progress-pct">{progress}%</span>
              </div>
              <p className="si-hero-progress-note">Estimated launch progress</p>
            </>
          )}

          {workingOn.length > 0 && (
            <div className="si-working-on">
              <span className="si-working-label">Currently working on</span>
              {workingOn.map((item) => (
                <span key={item} className="si-working-chip">{item}</span>
              ))}
            </div>
          )}

        </FadeIn>

        <div className="si-hero-deco" aria-hidden="true">
          {HERO_DECO.map((d, i) => (
            <div
              key={i}
              className="si-hero-deco-item"
              style={{ '--c': d.color, '--r': `${d.rotate}deg`, top: `${d.top}%`, left: `${d.left}%` }}
            >
              <McIcon materialKey={d.mat} size={52} />
            </div>
          ))}
        </div>
      </div>

      {season && (
        <div className="container">
          <FadeIn duration={0.7}>
            <div className="si-panel">
              {season.ip && (
                <>
                  <div className="si-panel-ip">
                    <span className="si-panel-ip-label">Server IP</span>
                    <span className="si-panel-ip-value">{season.ip}</span>
                    <button className="si-panel-ip-copy" onClick={copyIP}>
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  {stats.length > 0 && <div className="si-panel-divider" />}
                </>
              )}
              {stats.length > 0 && (
                <StaggerContainer className="si-stats-grid">
                  {stats.map(([label, value, material, color]) => (
                    <StaggerItem key={label}>
                      <div className="si-stat-card" style={{ '--sc': color }}>
                        <div className="si-stat-orb">
                          <McIcon materialKey={material} size={34} />
                        </div>
                        <div className="si-stat-text">
                          <span className="si-stat-label">{label}</span>
                          <span className="si-stat-value">{value}</span>
                        </div>
                      </div>
                    </StaggerItem>
                  ))}
                </StaggerContainer>
              )}
            </div>
          </FadeIn>
        </div>
      )}

      {visibleRoots.length > 0 && (
        <div className="si-roots-section">
          <div className="home-sec-head">
            <span className="sec-eyebrow">Season {season?.number ?? 2} feature</span>
            <h2 className="home-sec-title">{visibleRoots.length} <em>Roots</em> to master</h2>
          </div>

          <div className="si-roots-marquee">
            <div className="si-marquee-row">
              <div className="si-marquee-track">
                {[...marqueeA, ...marqueeA].map((r, i) => <RootChip key={`${r.slug}-${i}`} root={r} />)}
              </div>
            </div>
            <div className="si-marquee-row">
              <div className="si-marquee-track si-marquee-track--rev">
                {[...marqueeB, ...marqueeB].map((r, i) => <RootChip key={`${r.slug}-${i}`} root={r} />)}
              </div>
            </div>
          </div>

          <div className="si-roots-cta">
            <Link to="/wiki/roots" className="btn btn-primary" style={{ textDecoration: 'none' }}>
              Explore all Roots
            </Link>
          </div>
        </div>
      )}

      <div className="container section">
        <div className="home-sec-head">
          <span className="sec-eyebrow">Stay in the loop</span>
          <h2 className="home-sec-title">Latest <em>News & Offers</em></h2>
        </div>
        {news.length > 0 ? (
          <div className="home-news-grid">
            {news.slice(0, 3).map(item => <NewsCard key={item.id} item={item} />)}
          </div>
        ) : (
          <div className="home-news-empty">
            <p>No news or offers at the moment. Check back soon!</p>
          </div>
        )}
      </div>
    </>
  );
}
